<script lang="ts">
	import { MapPin, Mail, PhoneCall } from "@lucide/svelte";
	import { Button } from "@repo/ui/components/ui/button";
	import * as Drawer from "@repo/ui/components/ui/drawer";
	import { formatCurrency } from "@repo/ui/utils";
	import ContactForm from "./contactForm.svelte";
	import ImageGallery from "./imageGallery.svelte";
	import { MortgageCalculator } from "$lib/components/www/calculators/mortgage";
	import BathroomTooltip from "./bathroomTooltip.svelte";
	
let { data } = $props();
	let contactDrawerOpen = $state(false);
	let applyDrawerOpen = $state(false);
</script>

<div class="mx-auto max-w-[1400px] px-6 pb-24 pt-6 lg:px-8">
	<ImageGallery media={data.property!.media} />

	<!-- Property Details -->
	<div class="grid grid-cols-2 gap-8 pt-4 lg:grid-cols-3">
		<div class="col-span-2">
			<!-- Header Section -->
			<div id="property-header" class="mb-6">
				<div class=" grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
					<div class="order-2 md:order-none">
						<div class="mb-2 flex flex-wrap gap-2">
							{#if data.property?.status}
								<span
									class="rounded bg-green-100 px-2 py-1 text-xs font-medium uppercase text-green-600"
								>
									{data.property.status}
								</span>
							{/if}
							{#if data.property?.tags?.length}
								{#each data.property.tags as tag}
									<span
										class="bg-primary/10 text-primary rounded px-2 py-1 text-xs font-medium uppercase"
									>
										{tag}
									</span>
								{/each}
							{/if}
						</div>
						<div class="flex items-start gap-3">
							<MapPin class="text-primary/70 mt-1 h-6 w-6" />
							<div>
								<h1 class="text-2xl font-bold">
									{data.property?.title || "Property Details"}
								</h1>
								{#if data.property?.address}
									<p class="text-muted-foreground text-lg">
										{data.property.address.city}, {data.property.address.state}
										{data.property.address.zipCode}
									</p>
								{/if}
							</div>
						</div>
					</div>
					<div
						class="border-border/70 order-1 mt-auto rounded border border-b bg-gray-100/70 p-4 text-center text-gray-600 md:order-none md:border-b-0 md:pl-6 md:pr-6 md:text-right"
					>
						<span>Asking Price</span>
						<div class="text-4xl font-bold">
							{#if data.property?.price}
								{formatCurrency(data.property.price)}
							{:else}
								Contact for Price
							{/if}
						</div>
					</div>
				</div>
			</div>

			<!-- Key Features -->
			<div class="border-border bg-card mb-8 grid grid-cols-3 gap-4 rounded-lg border p-6">
				<div class="text-center">
					<div class="text-2xl font-bold">
						{data.property?.features?.bedrooms || "N/A"}
					</div>
					<div class="text-muted-foreground text-sm">Bedrooms</div>
				</div>
				<BathroomTooltip
					halfBathrooms={data.property?.features?.halfBathrooms || 0}
					fullBathrooms={data.property?.features?.fullBathrooms || 0}
					totalBathrooms={data.property?.features?.totalBathrooms || 0}
				/>
				<div class="text-center">
					{#if data.property?.propertyType === "Land" || data.property?.propertyType === "Farm & Ranch"}
						<div class="text-2xl font-bold">
							{data.property?.features?.squareFeet?.toLocaleString() || "N/A"}
						</div>
						<div class="text-muted-foreground text-sm">Acres</div>
					{:else}
						<div class="text-2xl font-bold">
							{data.property?.features?.squareFeet?.toLocaleString() || "N/A"}
						</div>
						<div class="text-muted-foreground text-sm">Square Feet</div>
					{/if}
				</div>
			</div>

			<!-- About the Property -->
			<div id="description" class="mb-8">
				<h2 class="mb-4 text-2xl font-bold">About the Property</h2>
				<p class="text-muted-foreground">
					{data.property?.description || "No description available."}
				</p>
			</div>

			<!-- Additional Features -->
			<div id="property-details" class="mb-8">
				<h2 class="mb-4 text-2xl font-bold">Property Details</h2>
				<div class="grid gap-4 sm:grid-cols-2">
					<div class="border-border bg-card rounded border p-4">
						<div class="text-muted-foreground text-sm font-medium">Property Type</div>
						<div class="mt-1 capitalize">{data.property?.propertySubType || "N/A"}</div>
					</div>
					<div class="border-border bg-card rounded border p-4">
						<div class="text-muted-foreground text-sm font-medium">Year Built</div>
						<div class="mt-1">{data.property?.features?.yearBuilt || "N/A"}</div>
					</div>
					<div class="border-border bg-card rounded border p-4">
						<div class="text-muted-foreground text-sm font-medium">Lot Size</div>
						<div class="mt-1">{data.property?.features?.lotSize || "N/A"}</div>
					</div>
					<div class="border-border bg-card rounded border p-4">
						<div class="text-muted-foreground text-sm font-medium">Garage</div>
						<div class="mt-1">
							{data.property?.features?.parking?.spaces || "N/A"} Parking Spaces
						</div>
					</div>
				</div>
			</div>

			{#if data.property?.matterportId}
				<div id="property-details" class="mb-8">
					<h2 class="mb-4 text-2xl font-bold">Virtual Tour</h2>
					<iframe
						title="Matterport Tour"
						class="aspect-video w-full"
						src={`https://my.matterport.com/show/?m=${data.property?.matterportId}`}
						frameborder="0"
						allowfullscreen
						allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
					></iframe>
				</div>
			{/if}
			<!-- Facts and Features
			<FactsAndFeatures />
			 -->

			<!-- Payment Calculator-->
			<MortgageCalculator price={data.property?.price || 0} />
		</div>

		<!-- Desktop Sidebar -->
		<div class="col-span-1 hidden space-y-6 md:block lg:sticky lg:top-6">
			<!-- Contact Section -->
			<div class="border-border bg-card rounded-lg border p-6">
				<div class="space-y-3">
					<div class="space-y-1">
						<h2 class="text-2xl font-bold">Get in Touch</h2>
						<p class="text-muted-foreground">
							Have questions or ready to see it in person, reach out and we'll be in
							touch shortly!
						</p>
					</div>

					<!-- Team Member Cards -->
					{#each data.salesTeam as teamMember}
						<div
							class="border-border flex flex-row gap-4 rounded-lg border bg-white p-3 shadow"
						>
							<div
								class="my-auto aspect-square w-1/3 overflow-hidden rounded-full border-2 border-white"
							>
								<img
									src={teamMember.headShot}
									alt={teamMember.fullName}
									class="aspect-square object-cover"
								/>
							</div>
							<div
								class="border-border flex w-full flex-col justify-center border-l pl-4"
							>
								<div class="text-foreground text-xl font-semibold leading-6">
									{teamMember.fullName}
								</div>
								<div class="text-muted-foreground">{teamMember.title}</div>
								<div
									class="border-muted-foreground/10 mt-2 flex flex-row gap-4 border-t pt-2"
								>
									<a
										href={`tel:${teamMember.phone}`}
										class="text-primary hover:text-primary/80"
									>
										<PhoneCall class="h-4 w-4" />
									</a>
									<a
										href={`mailto:${teamMember.email}`}
										class="text-primary hover:text-primary/80"
									>
										<Mail class="h-4 w-4" />
									</a>
								</div>
							</div>
						</div>
					{/each}

					<!-- Contact Form -->
					<div id="contact-form">
						<ContactForm
							data={data.contactForm}
							address={`${data.property?.address.street1}, ${data.property?.address.street2}, ${data.property?.address.city}, ${data.property?.address.state} ${data.property?.address.zipCode}`}
						/>
					</div>
				</div>
			</div>

			<!-- Get Qualified -->

			<div class="border-border bg-card rounded-lg border p-6">
				<div class="space-y-3">
					<div class="space-y-1">
						<h2 class="text-2xl font-bold">Get Qualified</h2>
						<p class="text-muted-foreground">
							Apply online with our preferred lender: The Green Team!
						</p>
					</div>
					<div class="flex justify-center">
						<img
							src="/tracigreen.webp"
							alt="traci green"
							class="my-6 w-2/3 rounded-lg"
						/>
					</div>

					<div class="flex flex-col gap-2">
						<h3 class="text-lg font-bold">Traci Green</h3>
						<p class="text-muted-foreground">Originating Branch Manager</p>
						<p class="text-muted-foreground">NMLS #270407</p>
						<div class="flex flex-row items-center gap-2">
							<PhoneCall class="h-4 w-4 text-[#0a8449]" />
							<p class="text-muted-foreground">(361)-676-0839</p>
						</div>
						<div class="flex flex-row items-center gap-2">
							<Mail class="h-4 w-4 text-[#0a8449]" />
							<p class="text-muted-foreground">traci@tracigreenteam.com</p>
						</div>
					</div>

					<Button
						class="w-full bg-[#0a8449] "
						href="https://apmobile.apmortgage.com/homehub/signup/traci@tracigreenteam.com"
						target="_blank">Apply Now</Button
					>
				</div>
			</div>
		</div>
	</div>
</div>

<!-- Mobile Drawer -->
<div
	class="border-border bg-card sticky bottom-0 border border-t p-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] md:hidden"
>
	<div class="space-y-4">
		<!-- Contact Drawer -->
		<Drawer.Root bind:open={contactDrawerOpen}>
			<Drawer.Trigger class="w-full">
				<div
					class="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded px-4 py-2 font-medium"
				>
					Get in Touch
				</div>
			</Drawer.Trigger>
			<Drawer.Content class="p-6">
				<!-- teammember cards -->
				{#each data.salesTeam as teamMember}
					<div
						class="border-border my-2 flex flex-row gap-4 rounded-lg border bg-white p-3 shadow"
					>
						<div
							class="my-auto aspect-square w-1/3 overflow-hidden rounded-full border-2 border-white"
						>
							<img
								src={teamMember.headShot}
								alt={teamMember.fullName}
								class="aspect-square object-cover"
							/>
						</div>
						<div
							class="border-border flex w-full flex-col justify-center border-l pl-4"
						>
							<div class="text-foreground text-xl font-semibold leading-6">
								{teamMember.fullName}
							</div>
							<div class="text-muted-foreground">{teamMember.title}</div>
							<div
								class="border-muted-foreground/10 mt-2 flex flex-row gap-4 border-t pt-2"
							>
								<a
									href={`tel:${teamMember.phone}`}
									class="text-primary hover:text-primary/80"
								>
									<PhoneCall class="h-4 w-4" />
								</a>
								<a
									href={`mailto:${teamMember.email}`}
									class="text-primary hover:text-primary/80"
								>
									<Mail class="h-4 w-4" />
								</a>
							</div>
						</div>
					</div>
				{/each}

				<Drawer.Title>Get in Touch</Drawer.Title>
				<Drawer.Description
					>Have questions or ready to see it in person, reach out and we'll be in touch
					shortly!</Drawer.Description
				>

				<ContactForm
					data={data.contactForm}
					address={`${data.property?.address.street1}, ${data.property?.address.street2}, ${data.property?.address.city}, ${data.property?.address.state} ${data.property?.address.zipCode}`}
					onSuccess={() => {
						contactDrawerOpen = !contactDrawerOpen;
					}}
				/>

				<Drawer.Close class="mt-2 w-full">
					<Button class="w-full" variant="outline">Close</Button>
				</Drawer.Close>
			</Drawer.Content>
		</Drawer.Root>

		<Drawer.Root bind:open={applyDrawerOpen}>
			<Drawer.Trigger class="w-full">
				<div
					class="text-primary-foreground hover:bg-primary/90 w-full rounded bg-[#0a8449] px-4 py-2 font-medium"
				>
					Get Qualified
				</div>
			</Drawer.Trigger>
			<Drawer.Content class="flex max-h-[90vh] flex-col overflow-y-auto">
				<Drawer.Header class="text-left">
					<Drawer.Title>Get Qualified</Drawer.Title>
					<Drawer.Description
						>Apply online with our preferred lender: The Green Team!
					</Drawer.Description>
					<div class="flex justify-center">
						<img
							src="/tracigreen.webp"
							alt="traci green"
							class="my-6 w-2/3 rounded-lg"
						/>
					</div>
					<div class="flex flex-col gap-2 text-center">
						<h3 class="text-lg font-bold">Traci Green</h3>
						<p class="text-muted-foreground">Originating Branch Manager</p>
						<p class="text-muted-foreground">NMLS #270407</p>
						<div class="flex flex-row items-center justify-center gap-2">
							<PhoneCall class="h-4 w-4 text-[#0a8449]" />
							<p class="text-muted-foreground">(361)-676-0839</p>
						</div>
						<div class="flex flex-row items-center justify-center gap-2">
							<Mail class="h-4 w-4 text-[#0a8449]" />
							<p class="text-muted-foreground">traci@tracigreenteam.com</p>
						</div>
					</div>
				</Drawer.Header>
				<div class="px-6">
					<Button
						class="w-full bg-[#0a8449] text-base font-medium "
						href="https://apmobile.apmortgage.com/homehub/signup/traci@tracigreenteam.com"
						target="_blank">Visit Site</Button
					>
				</div>
				<Drawer.Footer class="w-full px-6 pb-6">
					<Drawer.Close>
						<Button class="w-full" variant="outline">Close</Button>
					</Drawer.Close>
				</Drawer.Footer>
			</Drawer.Content>
		</Drawer.Root>
	</div>
</div>
